import { validateSchema } from '../../../../../../helpers';
import { goalSchema, targetSchema } from './validationSchema';
import {
  GoalTypes,
  TargetStages,
  TreatmentPlanResourceVersion,
  MasteryCriteriaChangeTypeEnum,
  BaselineMeasurementVersion,
  PCG_FOCUS_AREAS,
  BRG_FOCUS_AREAS,
} from '../constants/general';
import type {
  GoalValidationErrors,
  GoalValidationErrorsByType,
  GoalValidationTargetError,
} from '../types';

import type {
  IBehavior,
  ICommonDurationMasteryCriteriaItem,
  ICommonRateMasteryCriteriaItem,
  IInstanceMasteryCriteriaItem,
  IParencetageMasteryCriteriaItem,
  ITreatmentPlanGoal,
  ITrialSummary,
  ITrialSummaryTarget,
} from '../../../../../../utils/types';

const isTargetValidationErrors = (
  value: boolean | Record<string, GoalValidationTargetError>,
): value is Record<string, GoalValidationTargetError> => value !== true && value !== false;

export function filterBehaviorsByGoalType(
  behaviors: IBehavior[],
  goalType: GoalTypes | undefined,
): IBehavior[] {
  return behaviors.filter((b) => {
    if (goalType === GoalTypes.BehaviorReduction) {
      return BRG_FOCUS_AREAS.includes(b?.label?.trim()?.toLowerCase());
    } else if (goalType === GoalTypes.Caregiver) {
      return PCG_FOCUS_AREAS.includes(b?.label?.trim()?.toLowerCase());
    } else {
      const otherFocusAreas = [...BRG_FOCUS_AREAS, ...PCG_FOCUS_AREAS];
      return !otherFocusAreas.includes(b?.label?.trim()?.toLowerCase());
    }
  });
}

export const validateDurationMasteryCriteria = (
  masteryCriteria: ICommonDurationMasteryCriteriaItem,
  baseline_at_creation: number | null,
) => {
  const { change_type, desired_amount, practice_duration_count } = masteryCriteria;

  const errors: { [key: string]: string } = {};

  if (desired_amount === null || desired_amount === undefined) {
    errors.desired_amount = 'Desired amount is required';
    return errors;
  }

  if (!desired_amount && change_type === MasteryCriteriaChangeTypeEnum.INCREASE) {
    errors.desired_amount = 'Desired amount is required';
    return errors;
  }

  if (!practice_duration_count) {
    errors.practice_duration_count = 'Duration is required';
    return errors;
  }

  if (baseline_at_creation === null) {
    errors.baseline_at_creation = 'Please select baseline value';
    return errors;
  }

  if (
    change_type === MasteryCriteriaChangeTypeEnum.DECREASE &&
    desired_amount >= baseline_at_creation
  ) {
    errors.desired_amount = 'Desired amount must be less than baseline value';
    return errors;
  }

  if (
    change_type === MasteryCriteriaChangeTypeEnum.INCREASE &&
    desired_amount <= baseline_at_creation
  ) {
    errors.desired_amount = 'Desired amount must be greater than baseline value';
    return errors;
  }

  if (!practice_duration_count || practice_duration_count < 1) {
    errors.practice_duration_count = `Duration must be greater than 1`;
  }
  return errors;
};

export const validateRateMasteryCriteria = (
  masteryCriteria: ICommonRateMasteryCriteriaItem,
  baseline_at_creation: number | null,
) => {
  const { change_type, desired_amount, practice_duration_count } = masteryCriteria;

  const errors: { [key: string]: string } = {};

  if (desired_amount === null || desired_amount === undefined) {
    errors.desired_amount = 'Desired amount is required';
    return errors;
  }

  if (!desired_amount && change_type === MasteryCriteriaChangeTypeEnum.INCREASE) {
    errors.desired_amount = 'Desired amount is required';
    return errors;
  }

  if (!practice_duration_count) {
    errors.practice_duration_count = 'Duration is required';
    return errors;
  }

  if (baseline_at_creation === null) {
    errors.baseline_at_creation = 'Please select baseline value';
    return errors;
  }

  if (
    change_type === MasteryCriteriaChangeTypeEnum.DECREASE &&
    desired_amount >= baseline_at_creation
  ) {
    errors.desired_amount = 'Desired amount must be less than baseline value';
    return errors;
  }

  if (
    change_type === MasteryCriteriaChangeTypeEnum.INCREASE &&
    desired_amount <= baseline_at_creation
  ) {
    errors.desired_amount = 'Desired amount must be greater than baseline value';
    return errors;
  }

  if (!practice_duration_count || practice_duration_count < 1) {
    errors.practice_duration_count = `Duration must be greater than 1`;
  }
  return errors;
};

export const validateInstanceMasteryCriteria = (
  masteryCriteria: IInstanceMasteryCriteriaItem,
  baseline_at_creation: number | null,
  goalVersion: TreatmentPlanResourceVersion = TreatmentPlanResourceVersion.V2,
) => {
  const { change_type, desired_amount, practice_duration_count } = masteryCriteria;

  const errors: { [key: string]: string } = {};

  if (desired_amount === null || desired_amount === undefined) {
    errors.desired_amount = 'Desired amount is required';
    return errors;
  }

  if (!desired_amount && change_type === MasteryCriteriaChangeTypeEnum.INCREASE) {
    errors.desired_amount = 'Desired amount is required';
    return errors;
  }

  if (!practice_duration_count) {
    errors.practice_duration_count = 'Duration is required';
    return errors;
  }

  if (baseline_at_creation === null) {
    errors.baseline_at_creation = 'Please select baseline value';
    return errors;
  }

  if (
    change_type === MasteryCriteriaChangeTypeEnum.DECREASE &&
    desired_amount >= baseline_at_creation
  ) {
    errors.desired_amount = 'Desired amount must be less than baseline value';
    return errors;
  }

  if (
    change_type === MasteryCriteriaChangeTypeEnum.INCREASE &&
    desired_amount <= baseline_at_creation
  ) {
    errors.desired_amount = 'Desired amount must be greater than baseline value';
    return errors;
  }

  const changePercentage = Math.abs(baseline_at_creation - desired_amount) / baseline_at_creation;

  if (
    change_type === MasteryCriteriaChangeTypeEnum.DECREASE &&
    changePercentage < 0.1 &&
    goalVersion === TreatmentPlanResourceVersion.V2
  ) {
    errors.desired_amount = 'Reduction percentage must be at least 10%';
    return errors;
  }
  if (
    change_type === MasteryCriteriaChangeTypeEnum.INCREASE &&
    changePercentage < 0.1 &&
    goalVersion === TreatmentPlanResourceVersion.V2
  ) {
    errors.desired_amount = 'Increase percentage must be at least 10%';
    return errors;
  }
  if (!practice_duration_count || practice_duration_count < 3) {
    errors.practice_duration_count = `Duration must be greater than 2`;
  }
  return errors;
};

export const validateV2SAGMasteryCriteria = (
  masteryCriteria: IParencetageMasteryCriteriaItem,
  total_attempts: number | null,
  phase: 'acquisition' | 'generalization' | 'maintenance',
) => {
  const errors: { [key: string]: string } = {};
  const { desired_amount, practice_duration_count } = masteryCriteria;
  if (!desired_amount) {
    errors.desired_amount = 'Successful attempts are required';
    return errors;
  }
  if (!practice_duration_count) {
    errors.practice_duration_count = 'Duration count is required';
    return errors;
  }

  if ((phase === 'acquisition' || phase === 'generalization') && practice_duration_count <= 2) {
    errors.practice_duration_count = 'Duration must be greater than 2 sessions';
    return errors;
  }

  if (phase === 'maintenance' && practice_duration_count <= 8) {
    errors.practice_duration_count = 'Duration must be greater than 8 sessions';
    return errors;
  }

  if (desired_amount && total_attempts) {
    if (desired_amount > total_attempts) {
      errors.desired_amount = 'Successful attempts cannot exceed total attempts';
      return errors;
    }

    const successRate = desired_amount / total_attempts;
    if (successRate < 0.8) {
      errors.desired_amount = 'Success rate must be 80% or higher';
      return errors;
    }
  }
  return errors;
};

export const validatePCGMasteryCriteria = (
  masteryCriteria: IParencetageMasteryCriteriaItem,
  total_attempts: number | null,
  goalVersion: TreatmentPlanResourceVersion = TreatmentPlanResourceVersion.V2,
  baselineResourceVersion: BaselineMeasurementVersion = BaselineMeasurementVersion.V1,
) => {
  const errors: { [key: string]: string } = {};
  const { desired_amount, practice_duration_count } = masteryCriteria;
  if (!desired_amount) {
    errors.desired_amount = 'Successful attempts are required';
    return errors;
  }
  if (!practice_duration_count) {
    errors.practice_duration_count = 'Duration count is required';
    return errors;
  }

  if (
    baselineResourceVersion === BaselineMeasurementVersion.V2 &&
    goalVersion === TreatmentPlanResourceVersion.V2 &&
    practice_duration_count < 3
  ) {
    errors.practice_duration_count = 'Duration count should be greater than 2';
    return errors;
  }

  if (desired_amount && total_attempts) {
    if (desired_amount > total_attempts) {
      errors.desired_amount = 'Successful attempts cannot exceed total attempts';
      return errors;
    }

    // V1 SAG don't need it check for V2
    const successRate = desired_amount / total_attempts;
    if (successRate < 0.75 && goalVersion === TreatmentPlanResourceVersion.V2) {
      errors.desired_amount = 'Success rate must be 75% or higher';
      return errors;
    }
  }
  return errors;
};

export const getTargetTrialSummary = (
  tpGoalsTrialSummary: { [key: string]: ITrialSummary | null },
  goalId: number,
  targetId: number,
): ITrialSummaryTarget | null => {
  const goalSummaries = tpGoalsTrialSummary[goalId];
  if (!goalSummaries || !Array.isArray(goalSummaries) || goalSummaries.length === 0) return null;
  const goalSummary = goalSummaries[0];
  if (!goalSummary?.targets) return null;

  // Find the target within the goal's targets
  return goalSummary.targets.find((t: ITrialSummaryTarget) => t.target_id === targetId) || null;
};

export const getValidationErrorCount = (
  validationErrors: GoalValidationErrorsByType | null | undefined,
  goalType: GoalTypes,
) => {
  if (!validationErrors) return 0;

  const goalTypeMap = {
    [GoalTypes.BehaviorReduction]: 'behavior_reduction',
    [GoalTypes.SkillAcquisition]: 'skill_acquisition',
    [GoalTypes.Caregiver]: 'caregiver',
  };

  const goalErrors = validationErrors[goalTypeMap[goalType]];
  if (!goalErrors) return 0;

  let totalErrors = 0;

  Object.entries(goalErrors).forEach(([, goalErrorsObj]) => {
    const directErrors = Object.entries(goalErrorsObj).filter(
      ([key, isError]) => key !== 'measurement' && isError === true,
    ).length;

    const targetErrors = isTargetValidationErrors(goalErrorsObj.targets ?? false)
      ? Object.values(goalErrorsObj.targets).reduce(
          (acc, target) =>
            acc +
            (target === true || target === false
              ? Number(target)
              : Object.values(target).filter((isError) => isError === true).length),
          0,
        )
      : 0;
    totalErrors += directErrors + targetErrors;
  });

  return totalErrors;
};

export const validateGoalAndGetErrors = (
  data: ITreatmentPlanGoal,
  goalVersion: string,
  targetVersion = TreatmentPlanResourceVersion.V2,
) => {
  const errors: GoalValidationErrors = {};

  const {
    dirty: _dirty,
    is_editing: _is_editing,
    is_linkable: _is_linkable,
    expanded: _expanded,
    ...rest
  } = data;
  const restWithGoalVersion = { ...rest, goal_version: goalVersion };
  // Validate goal fields
  Object.keys(restWithGoalVersion).forEach((key) => {
    const isValidField = validateSchema(goalSchema, key, restWithGoalVersion);
    errors[key] = !isValidField;
  });

  // Validate targets
  if (data.targets?.length) {
    const targetErrors: { [key: number]: Record<string, boolean> } = {};
    data.targets.forEach((target, targetIndex) => {
      const targetData = {
        label: target.label,
        target_version: targetVersion,
        instruction: target.instruction,
        description: target.description,
        stage: target.stage,
      };
      const targetFieldErrors: Record<string, boolean> = {};
      Object.keys(targetData).forEach((key) => {
        const isValidField = validateSchema(targetSchema, key, targetData);
        if (!isValidField) {
          targetFieldErrors[key] = true;
        } else {
          targetFieldErrors[key] = false;
        }
      });
      //If the target stage log does not have a date_stage_changed, set the targetFieldErrors to true
      //Exception: If there's only one stage log entry with OnHold stage and null date
      if (
        target.id &&
        target.stage_change_logs?.some((log) => {
          const isOnlyOnHoldWithNullDate =
            target.stage_change_logs?.length === 1 &&
            log.new_stage === TargetStages.OnHold &&
            !log.date_stage_changed;

          return !log.date_stage_changed && !isOnlyOnHoldWithNullDate;
        })
      ) {
        targetFieldErrors.stage_change_log = true;
      }
      if (Object.keys(targetFieldErrors).length > 0) {
        targetErrors[targetIndex] = targetFieldErrors;
      }

      //For existing targets, if the stage_change_logs or stage is not null, set the stage to false
      if ((target.stage_change_logs && target.stage_change_logs.length > 0) || target.stage) {
        if (targetErrors[targetIndex]) {
          targetErrors[targetIndex].stage = false;
        }
      }
    });
    if (Object.keys(targetErrors).length > 0) {
      errors.targets = targetErrors;
    }
  }
  return errors;
};
